import { CROSS_ROAD_HALF, CROSS_SIGNAL, SIGNAL, STOP_LINE_OFFSET, type Junction } from '../../content/junctions';
import { activeRoute, directionOf, tripAt, worldS } from '../../world/route';

export type Signal = 'green' | 'amber' | 'red';

export interface JunctionPoint {
  seq: number;
  junction: Junction;
  /** Absolute distance of the crossing road's centre. */
  position: number;
  /** Where traffic on our road must stop on red. */
  stopLine: number;
}

const CYCLE = SIGNAL.green + SIGNAL.amber + SIGNAL.red;

/** Junctions in driving order: out trips pass them from the start, back trips from the end. */
export function junctionAt(seq: number): JunctionPoint {
  const { junctions } = activeRoute();
  const count = junctions.length;
  const trip = Math.floor(seq / count);
  const index = ((seq % count) + count) % count;
  const junction = directionOf(trip) === 'out' ? junctions[index] : junctions[count - 1 - index];
  if (!junction) throw new Error(`no junction ${seq}`);
  const position = worldS(trip, junction.at);
  return { seq, junction, position, stopLine: position - STOP_LINE_OFFSET };
}

/** The first junction whose crossing road is still ahead of (or under) position s. */
export function nextJunction(s: number): JunctionPoint {
  const count = activeRoute().junctions.length;
  for (let seq = Math.max(0, tripAt(s).trip) * count; ; seq += 1) {
    const point = junctionAt(seq);
    if (point.position + CROSS_ROAD_HALF > s) return point;
  }
}

/** Seconds into this junction's cycle. Each junction is offset so they don't all change together. */
function cycleTime(seq: number, time: number): number {
  const offset = (((seq * 7.3) % CYCLE) + CYCLE) % CYCLE;
  return (((time + offset) % CYCLE) + CYCLE) % CYCLE;
}

/** Our road's light. */
export function signalAt(seq: number, time: number): Signal {
  const t = cycleTime(seq, time);
  if (t < SIGNAL.green) return 'green';
  if (t < SIGNAL.green + SIGNAL.amber) return 'amber';
  return 'red';
}

/**
 * The crossing road's light: green only in the middle of our red, then amber, then all-red,
 * so the junction is empty when our green comes.
 */
export function crossSignalAt(seq: number, time: number): Signal {
  const t = cycleTime(seq, time);
  const crossStart = SIGNAL.green + SIGNAL.amber;
  const crossAmber = CYCLE - CROSS_SIGNAL.allRed - CROSS_SIGNAL.amber;
  if (t < crossStart) return 'red';
  if (t < crossAmber) return 'green';
  if (t < CYCLE - CROSS_SIGNAL.allRed) return 'amber';
  return 'red';
}

/** Crossing cars may move off their line only on their green. */
export function crossMayGo(seq: number, time: number): boolean {
  return crossSignalAt(seq, time) === 'green';
}

export function isInsideJunction(s: number, point: JunctionPoint): boolean {
  return Math.abs(s - point.position) < CROSS_ROAD_HALF;
}
